import React from 'react';
import { Icon } from 'semantic-ui-react';
import moment from 'moment-timezone';
import { FeedItem } from '../../types/FeedItem';
import { ResidentIdentityMap } from '../../hooks/useResidentIdentities';
import { normalizeId } from '../../../utils/normalizeId';

interface Props {
    item: FeedItem;
    onProfileClick?: (registrantId: string) => void;
    residentIdentityById?: ResidentIdentityMap;
    facilityTimezone?: string;
}

const parseInFacilityTz = (value: string | number, timezone?: string) =>
    timezone ? moment.tz(value, timezone) : moment(value);

const formatDate = (dateAdded?: string | number | null, timezone?: string) => {
    if (dateAdded === null || dateAdded === undefined) return 'at unknown time';

    let m = moment.invalid();

    if (typeof dateAdded === 'number' && Number.isFinite(dateAdded)) {
        const normalizedTimestamp = dateAdded < 1_000_000_000_000 ? dateAdded * 1000 : dateAdded;
        m = parseInFacilityTz(normalizedTimestamp, timezone);
    } else if (typeof dateAdded === 'string') {
        const rawValue = dateAdded.trim();
        if (!rawValue) return 'at unknown time';

        m = timezone
            ? moment.tz(rawValue, timezone)
            : moment(rawValue, moment.ISO_8601, true);
        if (!m.isValid()) {
            const numericDate = Number(rawValue);
            if (Number.isFinite(numericDate)) {
                // Support both seconds and milliseconds timestamps.
                const normalizedTimestamp = numericDate < 1_000_000_000_000 ? numericDate * 1000 : numericDate;
                m = parseInFacilityTz(normalizedTimestamp, timezone);
            }
        }
    }

    if (!m.isValid()) return 'at unknown time';

    return `at ${m.format('hh:mm A')} on ${m.format('MM/DD/YYYY')}`;
};

interface Actor {
    name?: string;
    image: string;
    isStaff: boolean;
}

const staffNameEl = (name?: string) => {
    const clean = (name || '').trim();
    if (!clean || /^[a-f0-9]{24}$/i.test(clean)) {
        return <span className="feed-staff-name">A staff member</span>;
    }
    return <>{'Staff '}<span className="feed-staff-name">{clean}</span></>;
};

const attendanceStatusLabel = (item: FeedItem) => {
    const raw = String(item.Action || item.Status || '').toLowerCase();
    if (raw === 'removed' || raw === 'declined') return 'declined';
    if (raw === 'attended') return 'attended';
    if (raw === 'waiting') return 'waiting';
    return 'registered';
};

const possessive = (name: string) => (/s$/i.test(name) ? `${name}'` : `${name}'s`);

const usableName = (name?: string | null) => {
    const clean = (name || '').trim();
    return clean && !/^[a-f0-9]{24}$/i.test(clean) ? clean : '';
};

// "Y's post" for a PostComment line. A resident author is shown by the directory (social profile)
// name found through PostAuthorId, like every other resident in the feed, else by the name saved on
// the line; a staff author gets the same "Staff <name>" form as staff actors. A missing or id-shaped
// name reads "a resident's post" / "a staff member's post"; lines without PostAuthorType keep the
// saved name or plain "a post".
const postOwnerPhrase = (item: FeedItem, authorIdentity?: { Name?: string }) => {
    // Same wording as the Posts tab (PostCard): a post whose author is no longer active is not named.
    if (item.PostAuthorIsActive === false) return "a former resident's post";
    const savedName = usableName(item.PostAuthorName);
    if (item.PostAuthorType === 'staff') {
        return `${savedName ? possessive(`Staff ${savedName}`) : "a staff member's"} post`;
    }
    if (item.PostAuthorType === 'resident') {
        return `${possessive(usableName(authorIdentity?.Name) || savedName || 'a resident')} post`;
    }
    return savedName ? `${possessive(savedName)} post` : 'a post';
};

// Single source of truth for "who did this" so the avatar and the rendered
// text can never disagree. Staff almost always act *on behalf of* a resident,
// so this only answers "who performed it" - the resident is still the subject
// of the sentence and has to be named separately. Support and Post are the
// categories where staff fields being present doesn't necessarily mean staff
// acted (e.g. a staff-facilitated approval on a resident's post can still
// stamp staff fields for audit purposes), so SupportAction/PostAuthorType
// disambiguate instead of the generic StaffId-presence heuristic below.
const resolveActor = (item: FeedItem, residentIdentity?: { Name?: string; Image?: string }): Actor => {
    if (item.Category === 'Support') {
        if (item.SupportAction === 'Resident') {
            return {
                name: residentIdentity?.Name || item.Registrant_FullName,
                image: residentIdentity?.Image || item.RegistrantImage || '/avatar_placeholder.png',
                isStaff: false,
            };
        }
        return { name: item.Staff_FullName, image: item.StaffImage || '/avatar_placeholder.png', isStaff: true };
    }
    if (item.Category === 'Post' && item.PostAuthorType) {
        if (item.PostAuthorType === 'resident') {
            return {
                name: residentIdentity?.Name || item.Registrant_FullName,
                image: residentIdentity?.Image || item.RegistrantImage || '/avatar_placeholder.png',
                isStaff: false,
            };
        }
        return { name: item.Staff_FullName, image: item.StaffImage || '/avatar_placeholder.png', isStaff: true };
    }
    // PostComment: StaffId / RegistrantId are the commenter's, and CommentAuthorType says which one
    // wrote it. Lines saved before that field existed fall through to the StaffId-presence check.
    if (item.Category === 'PostComment' && item.CommentAuthorType) {
        if (item.CommentAuthorType === 'resident') {
            return {
                name: residentIdentity?.Name || item.Registrant_FullName,
                image: residentIdentity?.Image || item.RegistrantImage || '/avatar_placeholder.png',
                isStaff: false,
            };
        }
        return { name: item.Staff_FullName, image: item.StaffImage || '/avatar_placeholder.png', isStaff: true };
    }
    if (item.Category === 'CommunityNews') {
        return { name: undefined, image: item.StaffImage || '/avatar_placeholder.png', isStaff: false };
    }
    if (item.Category === 'Announcement') {
        return { name: item.Staff_FullName, image: item.StaffImage || '/avatar_placeholder.png', isStaff: true };
    }
    if (item.StaffId || item.Staff_FullName) {
        return { name: item.Staff_FullName, image: item.StaffImage || '/avatar_placeholder.png', isStaff: true };
    }
    if (item.RegistrantId) {
        return {
            name: residentIdentity?.Name || item.Registrant_FullName,
            image: residentIdentity?.Image || item.RegistrantImage || '/avatar_placeholder.png',
            isStaff: false,
        };
    }
    return { name: undefined, image: '/avatar_placeholder.png', isStaff: false };
};

const FeedItemCard = ({ item, onProfileClick, residentIdentityById, facilityTimezone }: Props) => {
    // Feed rows freeze the resident's name/image at write time from their speak2
    // record; the directory-resolved social profile wins when we have it. Staff
    // rows keep their own image and are never in the map.
    const residentIdentity = residentIdentityById?.[normalizeId(item.RegistrantId)];
    const actor = resolveActor(item, residentIdentity);
    const avatar = actor.image;
    const dateStr = formatDate(item.DateAdded, facilityTimezone);

    const residentNameEl = (name?: string, id?: string) => {
        if (!name) return null;
        if (id && onProfileClick) {
            return (
                <span
                    className="feed-resident-name clickable"
                    role="button"
                    tabIndex={0}
                    onClick={() => onProfileClick(id)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onProfileClick(id);
                        }
                    }}
                >
                    {name}
                </span>
            );
        }
        return <span className="feed-resident-name">{name}</span>;
    };

    const renderContent = () => {
        const staffName = item.Staff_FullName;
        const residentName = residentIdentity?.Name || item.Registrant_FullName;
        const registrantId = item.RegistrantId;

        switch (item.Category) {
            case 'Announcement':
                return (
                    <span>
                        {staffNameEl(actor.name || staffName)}
                        {' sent an announcement '}
                        {item.AnnouncementMessage && <strong>{item.AnnouncementMessage}</strong>}
                        {' '}{dateStr}
                    </span>
                );
            case 'Request':
                if (item.Action === 'Closed' || item.Action === 'Cancel') {
                    return (
                        <span>
                            {residentNameEl(residentName, registrantId)}
                            {"'s request for "}
                            {item.RequestType && <strong>{item.RequestType}</strong>}
                            {item.Action === 'Cancel' ? ' was canceled ' : ' was closed '}
                            {dateStr}
                        </span>
                    );
                }
                return (
                    <span>
                        {residentNameEl(residentName, registrantId)}
                        {' requested '}
                        {item.RequestType && <strong>{item.RequestType}</strong>}
                        {' '}{dateStr}
                    </span>
                );
            case 'Attendance':
                // Staff fields are only written when a staff member marked it;
                // residents can mark their own attendance from the app.
                // Action/Status is intend_attend | attended | removed (and aliases).
                return (
                    <span>
                        {actor.isStaff
                            ? <>{staffNameEl(staffName)}{' marked '}{residentNameEl(residentName, registrantId)}{' as '}</>
                            : <>{residentNameEl(residentName, registrantId)}{' marked their attendance as '}</>}
                        <strong>{attendanceStatusLabel(item)}</strong>
                        {' for '}
                        {item.activityName && <strong>{item.activityName}</strong>}
                        {' '}{dateStr}
                    </span>
                );
            case 'CheckIn':
                return (
                    <span>
                        {actor.isStaff
                            ? (
                                <>
                                    {staffNameEl(staffName)}
                                    {item.CheckinMessage === 'checkin'
                                        ? <>{' checked in '}{residentNameEl(residentName, registrantId)}</>
                                        : <>{' marked '}{residentNameEl(residentName, registrantId)}{' as not checked in'}</>}
                                </>
                            )
                            : (
                                <>
                                    {residentNameEl(residentName, registrantId)}
                                    {item.CheckinMessage === 'checkin' ? ' checked in' : ' is not checked in'}
                                </>
                            )}
                        {' '}{dateStr}
                    </span>
                );
            case 'Greeting': {
                // Resident's own action - Hello! send. RegistrantId is set, no
                // staff fields, so resolveActor already treats this as a resident.
                const greetingCount = typeof item.GreetingRecipientCount === 'number'
                    ? item.GreetingRecipientCount
                    : (item.GreetingRecipientNames?.length || 0);
                const greetingContactLabel = greetingCount === 1 ? 'contact' : 'contacts';
                const greetingNames = (item.GreetingRecipientNames || []).join(', ');
                const greetingNamesSuffix = greetingNames
                    ? ` (${greetingNames.length > 60 ? `${greetingNames.slice(0, 60)}…` : greetingNames})`
                    : '';
                return (
                    <span>
                        {residentNameEl(residentName, registrantId) || 'A resident'}
                        {greetingCount > 0
                            ? ` greeted ${greetingCount} ${greetingContactLabel}${greetingNamesSuffix}`
                            : ' said hello (no contacts received it)'}
                        {item.GreetingMessage && <span>: <strong>{item.GreetingMessage}</strong></span>}
                        {' '}{dateStr}
                    </span>
                );
            }
            case 'Notification':
                return (
                    <span>
                        {staffNameEl(staffName)}
                        {' sent a notification '}
                        {item.NotificationMessage && <strong>{item.NotificationMessage}</strong>}
                        {' '}{dateStr}
                    </span>
                );
            case 'ServiceComment':
                return (
                    <span>
                        {actor.isStaff ? staffNameEl(staffName) : residentNameEl(residentName, registrantId)}
                        {' commented '}
                        {item.CommentText && <span>"{item.CommentText}"</span>}
                        {' '}{dateStr}
                    </span>
                );
            case 'WebRTC':
                return (
                    <span>
                        {item.isStaffUserSender && staffName && <span className="feed-staff-name">{staffName}</span>}
                        {item.isRelativeUserSender && item.Relative_FullName && <span className="feed-resident-name">{item.Relative_FullName}</span>}
                        {' made a video call'}
                        {residentName && <span> to {residentNameEl(residentName, registrantId)}</span>}
                        {' '}{dateStr}
                    </span>
                );
            case 'BasicRequest': {
                const isVisitingApartment = item.Action === 'Visiting Apartment' || item.Action === 'Visiting_Apartment';
                const isLeavingApartment = item.Action === 'Leaving Apartment' || item.Action === 'Leaving_Apartment';
                if (isVisitingApartment || isLeavingApartment) {
                    return (
                        <span>
                            {'Staff '}
                            {staffName && <span className="feed-staff-name">{staffName}</span>}
                            {isVisitingApartment ? ' is visiting apartment ' : ' is leaving apartment '}
                            {item.Unit_Name && <strong>{item.Unit_Name}</strong>}
                            {' '}{dateStr}
                        </span>
                    );
                }
                // Otherwise the resident's own request. Details is unset here; Action holds the request type.
                return (
                    <span>
                        {residentNameEl(residentName, registrantId) || 'A resident'}
                        {' made a request'}
                        {(item.Action || item.Details) && <span>: {item.Action || item.Details}</span>}
                        {' '}{dateStr}
                    </span>
                );
            }
            case 'Post':
                return (
                    <span>
                        {actor.isStaff ? staffNameEl(staffName) : residentNameEl(residentName, registrantId)}
                        {' created a post'}
                        {item.PostPreview && <span>: {stripMentions(item.PostPreview)}</span>}
                        {' '}{dateStr}
                    </span>
                );
            case 'PostComment': {
                // The commenter is the actor (resolveActor); PostAuthorName / PostAuthorId are who wrote the post.
                const postOwnerIdentity = residentIdentityById?.[normalizeId(item.PostAuthorId)];
                return (
                    <span>
                        {actor.isStaff ? staffNameEl(staffName) : residentNameEl(residentName, registrantId)}
                        {' commented on '}
                        {postOwnerPhrase(item, postOwnerIdentity)}
                        {' '}{dateStr}
                    </span>
                );
            }
            case 'NfcPresence':
                return (
                    <span>
                        {residentNameEl(residentName, registrantId)}
                        {item.Action === 'LastLocation' && item.LastLocation
                            ? <span> was last seen at <strong>{item.LastLocation}</strong></span>
                            : <span> presence detected</span>
                        }
                        {' '}{dateStr}
                    </span>
                );
            case 'Premise':
                // Staff details are deliberately omitted by the backend when the
                // resident toggled this themselves, so staff presence here means
                // a staff member changed it on the resident's behalf.
                return (
                    <span>
                        {actor.isStaff
                            ? (
                                <>
                                    {staffNameEl(staffName)}
                                    {' marked '}
                                    {residentNameEl(residentName, registrantId)}
                                    {item.Action === 'On' ? ' as on premise' : ' as off premise'}
                                </>
                            )
                            : (
                                <>
                                    {residentNameEl(residentName, registrantId)}
                                    {item.Action === 'On' ? ' came on premise' : ' went off premise'}
                                </>
                            )}
                        {' '}{dateStr}
                    </span>
                );
            case 'MailIsIn':
                // Facility-wide announcement; recipients live in residentIds,
                // there is no single resident subject.
                return (
                    <span>
                        {staffNameEl(staffName)}
                        {' announced the mail is in'}
                        {' '}{dateStr}
                    </span>
                );
            case 'Invites':
                return (
                    <span>
                        {staffNameEl(staffName)}
                        {' invited '}
                        {item.inviteTo ? <strong>{item.inviteTo}</strong> : 'a family member'}
                        {residentName && <span> to connect with {residentNameEl(residentName, registrantId)}</span>}
                        {' '}{dateStr}
                    </span>
                );
            case 'AddressBook':
                return (
                    <span>
                        {staffNameEl(staffName)}
                        {item.Action === 'Remove' ? ' removed contact ' : ' added contact '}
                        {item.Relative_FullName && <strong>{item.Relative_FullName}</strong>}
                        {residentName && <span> for {residentNameEl(residentName, registrantId)}</span>}
                        {' '}{dateStr}
                    </span>
                );
            case 'CommunityMessage':
                return (
                    <span>
                        {staffNameEl(staffName)}
                        {' posted a community message'}
                        {' '}{dateStr}
                    </span>
                );
            case 'CommunityNews':
                return (
                    <span>
                        {'Community News '}
                        {item.Details && <strong>{item.Details}</strong>}
                        {' '}{dateStr}
                    </span>
                );
            case 'NotePad':
                return (
                    <span>
                        {staffNameEl(staffName)}
                        {item.Action === 'Remove' ? ' removed a note' : ' added a note'}
                        {residentName && <span> for {residentNameEl(residentName, registrantId)}</span>}
                        {' '}{dateStr}
                    </span>
                );
            case 'Support':
                // SupportAction is authoritative here - staff fields are filled
                // from the authenticated user even on resident-submitted requests.
                return (
                    <span>
                        {actor.isStaff ? staffNameEl(staffName) : residentNameEl(residentName, registrantId)}
                        {' submitted a support request'}
                        {' '}{dateStr}
                    </span>
                );
            case 'Pals':
                // SenderId is the relative, ReceiverId the resident: staff are
                // approving a message sent to the resident by their family.
                return (
                    <span>
                        {staffNameEl(staffName)}
                        {item.palsMessageRequest === 'declined' ? ' declined a message from ' : ' approved a message from '}
                        {item.Relative_FullName ? <strong>{item.Relative_FullName}</strong> : 'a family member'}
                        {residentName && <span> for {residentNameEl(residentName, registrantId)}</span>}
                        {' '}{dateStr}
                    </span>
                );
            case 'DeviceMove':
                // Tied to a unit, not a resident - no resident fields are written.
                return (
                    <span>
                        {staffNameEl(staffName)}
                        {' moved a device'}
                        {item.Unit_Name && <span> to {item.Unit_Name}</span>}
                        {' '}{dateStr}
                    </span>
                );
            case 'DeviceStatusMonitor':
                // System event with no actor; resident is attached only when the
                // unit has one.
                return (
                    <span>
                        {residentName
                            ? <>{residentNameEl(residentName, registrantId)}{"'s device"}</>
                            : <>{'The device'}{item.Unit_Name && <span> in {item.Unit_Name}</span>}</>}
                        {item.DeviceStatus && <span> is <strong>{item.DeviceStatus}</strong></span>}
                        {' '}{dateStr}
                    </span>
                );
            default:
                return (
                    <span>
                        {actor.isStaff
                            ? staffNameEl(staffName)
                            : (residentNameEl(residentName, registrantId) || 'Unknown')}
                        {' - '}{item.Category}
                        {item.Details && <span>: {item.Details}</span>}
                        {' '}{dateStr}
                    </span>
                );
        }
    };

    return (
        <div className="feed-item">
            <img
                src={avatar}
                alt=""
                className="feed-item-avatar"
                onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = '/avatar_placeholder.png';
                }}
            />
            <div className="feed-item-body">
                <div className="feed-item-text">
                    {renderContent()}
                </div>
            </div>
            {item.RegistrantId && onProfileClick && (
                <div
                    className="feed-item-profile"
                    role="button"
                    tabIndex={0}
                    onClick={() => onProfileClick(item.RegistrantId!)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onProfileClick(item.RegistrantId!);
                        }
                    }}
                >
                    <Icon name="user" />
                </div>
            )}
        </div>
    );
};

// Strip raw mention syntax like @[Name](resident:id) → Name
const stripMentions = (text: string) => {
    return text.replace(/@\[([^\]]+)\]\([^)]+\)/g, '$1');
};

export default FeedItemCard;
